// ==================== HOME PAGE ====================

import { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  MessageSquare, 
  Users, 
  Send, 
  BarChart3, 
  Zap, 
  Shield,
  CheckCircle2,
  Star,
  Bell,
  TrendingUp,
  Target,
  Heart,
  Sparkles,
  Rocket,
  Bot,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { FloatingChatBubble } from '../../components/ui/FloatingCard';
import { VideoPlayer, VideoTriggerButton } from '../../components/ui/VideoPlayer';
import { ROUTES } from '../../constants';
import { cn } from '../../utils/helpers';
import { usePlans } from '../../hooks';
import {
  fadeInUp,
  fadeInRight,
  scaleIn,
  staggerContainer,
  pulseTransition,
  hoverScale,
  tapScale,
  hoverLift,
  hoverRotate,
  backgroundGlow
} from '../../utils/animations';
import heroGirlImg from '../../assets/hero_girl_img.png';
import { ClientsMarquee } from '../../components/ui/ClientsMarquee';
import { WhatsAppSuiteSlider } from '../../components/ui/WhatsAppSuiteSlider';

// WhatsApp SVG icon — used across sections for platform branding
const WhatsAppIcon = ({ size = 20, className = '' }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Pre-computed particle positions to avoid Math.random in render
const PARTICLE_POSITIONS = [
  { top: 25, left: 15, duration: 3.5 },
  { top: 45, left: 35, duration: 4.2 },
  { top: 65, left: 55, duration: 3.8 },
  { top: 35, left: 75, duration: 4.5 },
  { top: 55, left: 25, duration: 3.2 },
  { top: 75, left: 85, duration: 4.0 },
  { top: 20, left: 65, duration: 3.6 },
  { top: 80, left: 45, duration: 4.3 },
];

// Floating Icon Component for background decorations
const FloatingIcon = ({ 
  icon: Icon, 
  className, 
  delay = 0,
  duration = 4,
  size = 20
}: { 
  icon: React.ElementType; 
  className: string; 
  delay?: number;
  duration?: number;
  size?: number;
}) => (
  <motion.div
    className={`absolute ${className}`}
    initial={{ opacity: 0, scale: 0 }}
    animate={{ 
      opacity: [0.2, 0.5, 0.2],
      scale: [0.8, 1, 0.8],
      y: [-10, 10, -10],
    }}
    transition={{ 
      duration, 
      repeat: Infinity, 
      delay,
      ease: [0.45, 0.05, 0.55, 0.95]
    }}
  >
    <div className="p-3 rounded-xl bg-(--color-charcoal-black)/50 backdrop-blur-sm border border-(--color-border-gray)/30">
      <Icon size={size} className="text-(--color-signal-orange)" />
    </div>
  </motion.div>
);

// Hero Section with Girl Image and Vectors
const HeroSection = () => {
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  return (
  <section className="relative overflow-hidden py-16 lg:py-24 min-h-[90vh] flex items-center">
    {/* Background Effects */}
    <div className="absolute inset-0 bg-linear-to-b from-(--color-midnight-navy) via-(--color-dark-navy) to-(--color-midnight-navy)" />
    
    {/* Animated Background Orbs */}
    <motion.div 
      className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-(--color-signal-orange)/8 rounded-full blur-3xl"
      animate={{ 
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3]
      }}
      transition={{ duration: 8, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
    />
    <motion.div 
      className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-(--color-warm-amber)/5 rounded-full blur-3xl"
      animate={{ 
        scale: [1.2, 1, 1.2],
        opacity: [0.2, 0.4, 0.2]
      }}
      transition={{ duration: 10, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
    />
    <motion.div 
      className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-(--color-signal-orange)/5 rounded-full blur-2xl"
      animate={{ 
        scale: [1, 1.3, 1],
        x: [-20, 20, -20],
        opacity: [0.2, 0.4, 0.2]
      }}
      transition={{ duration: 12, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
    />
    
    {/* Grid Pattern Overlay */}
    <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: `linear-gradient(rgba(230,142,55,0.1) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(230,142,55,0.1) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    }} />

    {/* Floating Background Icons - Left Side */}
    <FloatingIcon icon={WhatsAppIcon} className="top-[15%] left-[5%] hidden lg:block" delay={0} duration={5} />
    <FloatingIcon icon={Send} className="top-[35%] left-[8%] hidden lg:block" delay={0.5} duration={4.5} />
    <FloatingIcon icon={WhatsAppIcon} className="top-[60%] left-[3%] hidden lg:block" delay={1} duration={5.5} />
    <FloatingIcon icon={MessageSquare} className="top-[80%] left-[10%] hidden lg:block" delay={1.5} duration={4} />
    
    {/* Floating Background Icons - Right Side */}
    <FloatingIcon icon={WhatsAppIcon} className="top-[10%] right-[8%] hidden lg:block" delay={0.3} duration={4.8} />
    <FloatingIcon icon={Bot} className="top-[25%] right-[3%] hidden lg:block" delay={0.8} duration={5.2} />
    <FloatingIcon icon={WhatsAppIcon} className="top-[75%] right-[5%] hidden lg:block" delay={1.3} duration={4.3} />
    <FloatingIcon icon={Sparkles} className="top-[55%] right-[10%] hidden xl:block" delay={1.8} duration={5} />
    
    {/* Animated Particles/Dots */}
    {PARTICLE_POSITIONS.map((particle, i) => (
      <motion.div
        key={`particle-${i}`}
        className="absolute w-1 h-1 rounded-full bg-(--color-signal-orange) hidden lg:block"
        style={{
          top: `${particle.top}%`,
          left: `${particle.left}%`,
        }}
        animate={{
          y: [-20, 20, -20],
          x: [-10, 10, -10],
          opacity: [0, 0.6, 0],
          scale: [0, 1.5, 0],
        }}
        transition={{
          duration: particle.duration,
          repeat: Infinity,
          delay: i * 0.4,
          ease: [0.45, 0.05, 0.55, 0.95],
        }}
      />
    ))}
    
    {/* Animated Connection Lines */}
    <svg className="absolute inset-0 w-full h-full pointer-events-none hidden lg:block" style={{ zIndex: 1 }}>
      <motion.path
        d="M100,200 Q200,100 300,200 T500,200"
        stroke="url(#lineGradient)"
        strokeWidth="1"
        fill="none"
        strokeDasharray="5,5"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 0.3 }}
        transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
      />
      <motion.path
        d="M1200,300 Q1100,400 1000,300 T800,300"
        stroke="url(#lineGradient)"
        strokeWidth="1"
        fill="none"
        strokeDasharray="5,5"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 0.3 }}
        transition={{ duration: 4, repeat: Infinity, repeatType: "reverse", delay: 1 }}
      />
      <defs>
        <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="var(--color-signal-orange)" stopOpacity="0" />
          <stop offset="50%" stopColor="var(--color-signal-orange)" stopOpacity="0.5" />
          <stop offset="100%" stopColor="var(--color-signal-orange)" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>

    <div className="container-custom relative z-10">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        {/* Left Content */}
        <motion.div 
          className="text-center lg:text-left"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Badge */}
          <motion.div variants={fadeInUp}>
            <Badge variant="orange" className="mb-6 inline-flex">
              <motion.span 
                animate={{ scale: [1, 1.05, 1] }}
                transition={pulseTransition}
                className="mr-2"
              >
                🚀
              </motion.span>
              Now with AI-powered automation
            </Badge>
          </motion.div>

          {/* Heading */}
          <motion.h1 
            variants={fadeInUp}
            className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6"
          >
            Amplify Your
            <motion.span 
              className="block text-gradient mt-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              WhatsApp Conversations
            </motion.span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            variants={fadeInUp}
            className="text-lg md:text-xl text-(--color-soft-gray) max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed"
          >
            Transform your business communications into measurable growth with our powerful WhatsApp Business API automation platform.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-8"
          >
            <Link to={ROUTES.AUTH.REGISTER}>
              <motion.div whileHover={hoverScale} whileTap={tapScale}>
                <Button variant="primary" size="lg" rightIcon={<ArrowRight size={18} />}>
                  Get Started
                </Button>
              </motion.div>
            </Link>
            <VideoTriggerButton onClick={() => setIsVideoOpen(true)} />
          </motion.div>

          {/* Trust Indicators */}
          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-6 text-sm text-(--color-muted-gray)"
          >
            {[
              'Quick setup in minutes',
              'No hidden fees',
              'Cancel anytime'
            ].map((text, index) => (
              <motion.div 
                key={index}
                className="flex items-center gap-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
              >
                <CheckCircle2 size={16} className="text-(--color-success)" />
                <span>{text}</span>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right Content - Hero Image with Floating Elements */}
        <motion.div 
          className="relative flex justify-center lg:justify-end"
          initial="hidden"
          animate="visible"
          variants={fadeInRight}
        >
          {/* Main Hero Image Container */}
          <div className="relative">
            {/* Glowing Circle Behind */}
            <motion.div 
              className="absolute -inset-4 bg-linear-to-br from-(--color-signal-orange)/20 to-(--color-warm-amber)/10 rounded-full blur-2xl"
              animate={backgroundGlow}
              transition={{ duration: 4, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
            />
            
            {/* Hero Girl Image */}
            <motion.img
              src={heroGirlImg}
              alt="Professional using AmplifyMsg"
              className="relative z-10 w-full max-w-md lg:max-w-lg xl:max-w-xl h-auto object-contain drop-shadow-2xl"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            />

            {/* Floating Chat Bubble - Top Right */}
            <FloatingChatBubble 
              className="top-8 -right-4 lg:right-0"
              delay={1}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#25D366] flex items-center justify-center">
                  <WhatsAppIcon size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-white">WhatsApp</p>
                  <p className="text-[10px] text-(--color-muted-gray)">Campaign delivered!</p>
                </div>
              </div>
            </FloatingChatBubble>

            {/* Floating Stats Card - Left */}
            <FloatingChatBubble 
              className="-left-4 lg:-left-12 top-1/3"
              delay={1.2}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-(--color-success)/20 flex items-center justify-center">
                  <TrendingUp size={18} className="text-(--color-success)" />
                </div>
                <div>
                  <p className="text-lg font-bold text-white">+127%</p>
                  <p className="text-[10px] text-(--color-muted-gray)">Engagement Rate</p>
                </div>
              </div>
            </FloatingChatBubble>

            {/* Floating Campaign Card - Bottom Right */}
            <FloatingChatBubble 
              className="bottom-24 -right-2 lg:right-4"
              delay={1.4}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-(--color-info)/20 flex items-center justify-center">
                  <Target size={14} className="text-(--color-info)" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-white">Campaign Live</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="w-1.5 h-1.5 bg-(--color-success) rounded-full animate-pulse" />
                    <p className="text-[10px] text-(--color-success)">5.2k reached</p>
                  </div>
                </div>
              </div>
            </FloatingChatBubble>

            {/* Floating Notification - Bottom Left */}
            <FloatingChatBubble 
              className="bottom-8 -left-2 lg:left-0"
              delay={1.6}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-(--color-warning)/20 flex items-center justify-center">
                  <Bell size={14} className="text-(--color-warning)" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-white">98% Delivered</p>
                  <p className="text-[10px] text-(--color-muted-gray)">Last 24 hours</p>
                </div>
              </div>
            </FloatingChatBubble>

            {/* New: Floating AI Badge - Top Left */}
            <motion.div
              className="absolute top-16 -left-8 lg:-left-16 hidden md:block"
              initial={{ opacity: 0, scale: 0, rotate: -10 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: 1.8, duration: 0.5 }}
            >
              <motion.div
                className="px-3 py-2 rounded-xl bg-linear-to-br from-(--color-signal-orange) to-(--color-warm-amber) shadow-lg"
                animate={{ y: [-5, 5, -5], rotate: [-2, 2, -2] }}
                transition={{ duration: 4, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
              >
                <div className="flex items-center gap-2">
                  <Bot size={16} className="text-white" />
                  <span className="text-xs font-semibold text-white">AI Powered</span>
                </div>
              </motion.div>
            </motion.div>

            {/* New: Floating Heart/Like Animation */}
            <motion.div
              className="absolute top-1/2 -right-12 lg:-right-20 hidden md:block"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2 }}
            >
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={`heart-${i}`}
                  className="absolute"
                  initial={{ opacity: 0, y: 0, scale: 0.5 }}
                  animate={{ 
                    opacity: [0, 1, 0],
                    y: [-20 * i, -60 - 20 * i],
                    scale: [0.5, 1, 0.5],
                    x: [0, (i % 2 === 0 ? 10 : -10), 0]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: i * 0.7,
                    ease: [0.45, 0.05, 0.55, 0.95],
                  }}
                >
                  <Heart size={14} className="text-(--color-signal-orange) fill-current" />
                </motion.div>
              ))}
            </motion.div>

            {/* New: Floating Rocket */}
            <motion.div
              className="absolute -top-4 right-1/4 hidden lg:block"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.2 }}
            >
              <motion.div
                animate={{ 
                  y: [-8, 8, -8],
                  rotate: [0, 5, 0, -5, 0],
                }}
                transition={{ duration: 3, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
              >
                <div className="p-2 rounded-full bg-(--color-charcoal-black)/80 border border-(--color-signal-orange)/30">
                  <Rocket size={18} className="text-(--color-signal-orange)" />
                </div>
              </motion.div>
            </motion.div>

            {/* New: Animated Ring Around Image */}
            <motion.div
              className="absolute inset-0 -m-8 rounded-full border-2 border-dashed border-(--color-signal-orange)/20 hidden lg:block"
              animate={{ rotate: 360 }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            />
            <motion.div
              className="absolute inset-0 -m-16 rounded-full border border-(--color-warm-amber)/10 hidden lg:block"
              animate={{ rotate: -360 }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            />

            {/* New: Sparkle Effects */}
            {[...Array(4)].map((_, i) => (
              <motion.div
                key={`sparkle-${i}`}
                className="absolute hidden md:block"
                style={{
                  top: `${20 + i * 20}%`,
                  right: `${-5 + (i % 2) * 10}%`,
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ 
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: i * 0.5 + 2.5,
                  ease: [0.45, 0.05, 0.55, 0.95],
                }}
              >
                <Sparkles size={12} className="text-(--color-signal-orange)" />
              </motion.div>
            ))}

            {/* Decorative Dots */}
            <motion.div 
              className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2 }}
            >
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  className="w-2 h-2 rounded-full bg-(--color-signal-orange)"
                  animate={{ 
                    scale: [1, 1.5, 1],
                    opacity: [0.3, 1, 0.3]
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity, 
                    delay: i * 0.2,
                    ease: [0.45, 0.05, 0.55, 0.95]
                  }}
                />
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>

    {/* Bottom Wave - Animated */}
    <div className="absolute bottom-0 left-0 right-0 h-32 overflow-hidden">
      {/* Wave Layer 1 - Front */}
      <motion.div
        className="absolute bottom-0 left-0 w-[200%] h-full"
        animate={{ x: [0, '-50%'] }}
        transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
      >
        <svg className="w-full h-full" viewBox="0 0 2880 100" preserveAspectRatio="none">
          <path
            d="M0,60 C240,20 480,80 720,60 C960,40 1200,80 1440,60 C1680,40 1920,80 2160,60 C2400,40 2640,80 2880,60 L2880,100 L0,100 Z"
            fill="url(#heroWave1)"
            opacity="0.4"
          />
          <defs>
            <linearGradient id="heroWave1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--color-signal-orange)" />
              <stop offset="50%" stopColor="var(--color-warm-amber)" />
              <stop offset="100%" stopColor="var(--color-signal-orange)" />
            </linearGradient>
          </defs>
        </svg>
      </motion.div>
      
      {/* Wave Layer 2 - Middle */}
      <motion.div
        className="absolute bottom-0 left-0 w-[200%] h-full"
        animate={{ x: ['-50%', 0] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
      >
        <svg className="w-full h-full" viewBox="0 0 2880 100" preserveAspectRatio="none">
          <path
            d="M0,70 C360,30 720,90 1080,70 C1440,50 1800,90 2160,70 C2520,50 2880,90 2880,70 L2880,100 L0,100 Z"
            fill="url(#heroWave2)"
            opacity="0.25"
          />
          <defs>
            <linearGradient id="heroWave2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--color-warm-amber)" />
              <stop offset="50%" stopColor="var(--color-signal-orange)" />
              <stop offset="100%" stopColor="var(--color-warm-amber)" />
            </linearGradient>
          </defs>
        </svg>
      </motion.div>
      
      {/* Wave Layer 3 - Back */}
      <motion.div
        className="absolute bottom-0 left-0 w-[200%] h-full"
        animate={{ x: [0, '-50%'] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
      >
        <svg className="w-full h-full" viewBox="0 0 2880 100" preserveAspectRatio="none">
          <path
            d="M0,80 C480,50 960,100 1440,80 C1920,60 2400,100 2880,80 L2880,100 L0,100 Z"
            fill="url(#heroWave3)"
            opacity="0.15"
          />
          <defs>
            <linearGradient id="heroWave3" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--color-signal-orange)" />
              <stop offset="100%" stopColor="var(--color-warm-amber)" />
            </linearGradient>
          </defs>
        </svg>
      </motion.div>
      
      {/* Animated Glow Effect */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-1 bg-linear-to-r from-transparent via-(--color-signal-orange) to-transparent"
        animate={{ opacity: [0.3, 0.7, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
      />
    </div>

    {/* Video Player Modal */}
    <VideoPlayer
      isOpen={isVideoOpen}
      onClose={() => setIsVideoOpen(false)}
      videoId="V4yP7TG6sr4"
      title="AmplifyMsg Platform Demo"
      subtitle="See how AmplifyMsg can transform your business communications"
      autoPlay={true}
    />
  </section>
  );
};

// Stats Section with Animation
const StatsSection = () => {
  const stats = [
    { value: '10M+', label: 'Messages Sent', hasWaIcon: true },
    { value: '5,000+', label: 'Happy Customers', hasWaIcon: false },
    { value: '99.9%', label: 'Uptime SLA', hasWaIcon: false },
    { value: '50+', label: 'Countries', hasWaIcon: false },
  ];

  return (
    <section className="py-16 bg-(--color-dark-navy) relative overflow-hidden">
      {/* Subtle WhatsApp watermark */}
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.02] pointer-events-none"
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
      >
        <WhatsAppIcon size={300} className="text-[#25D366]" />
      </motion.div>

      <div className="container-custom relative z-10">
        <motion.div 
          className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {stats.map((stat, index) => (
            <motion.div 
              key={index} 
              className="text-center"
              variants={scaleIn}
            >
              <motion.div 
                className="text-3xl lg:text-4xl font-heading font-bold text-gradient mb-2 flex items-center justify-center gap-2"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                {stat.hasWaIcon && (
                  <motion.div
                    animate={{ scale: [1, 1.15, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
                  >
                    <WhatsAppIcon size={28} className="text-[#25D366]" />
                  </motion.div>
                )}
                {stat.value}
              </motion.div>
              <div className="text-sm text-(--color-muted-gray)">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Features Section with Animation
const FeaturesSection = () => {
  const features = [
    {
      icon: MessageSquare,
      title: 'Smart Messaging',
      description: 'Send personalized messages at scale with our intelligent automation engine.',
    },
    {
      icon: Users,
      title: 'Contact Management',
      description: 'Organize and segment your contacts for targeted campaigns.',
    },
    {
      icon: Send,
      title: 'Campaign Builder',
      description: 'Create and schedule powerful marketing campaigns in minutes.',
    },
    {
      icon: BarChart3,
      title: 'Advanced Analytics',
      description: 'Track performance with real-time dashboards and insights.',
    },
    {
      icon: Zap,
      title: 'Automation Flows',
      description: 'Build automated workflows triggered by customer actions.',
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-grade encryption and compliance with global standards.',
    },
  ];

  return (
    <section className="py-20 lg:py-28 relative overflow-hidden">
      {/* Floating WhatsApp background accents */}
      <motion.div
        className="absolute top-20 right-[8%] hidden lg:block opacity-[0.04]"
        animate={{ y: [-15, 15, -15], rotate: [-5, 5, -5] }}
        transition={{ duration: 8, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
      >
        <WhatsAppIcon size={120} className="text-[#25D366]" />
      </motion.div>
      <motion.div
        className="absolute bottom-20 left-[5%] hidden lg:block opacity-[0.04]"
        animate={{ y: [10, -10, 10], rotate: [3, -3, 3] }}
        transition={{ duration: 10, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
      >
        <WhatsAppIcon size={90} className="text-[#25D366]" />
      </motion.div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center max-w-2xl mx-auto mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <Badge variant="orange" className="mb-4">
            <WhatsAppIcon size={14} className="mr-1.5 inline-block -mt-0.5" />
            Features
          </Badge>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
            Everything You Need to
            <span className="text-gradient block">Scale Communications</span>
          </h2>
          <p className="text-(--color-muted-gray)">
            Powerful tools designed to help businesses amplify their customer conversations.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              whileHover={hoverLift}
            >
              <Card hoverable className="group h-full">
                <motion.div 
                  className="w-12 h-12 rounded-xl bg-(--color-orange-glow) flex items-center justify-center mb-4 group-hover:bg-(--color-signal-orange) transition-colors duration-300"
                  whileHover={hoverRotate}
                >
                  <feature.icon size={24} className="text-(--color-signal-orange) group-hover:text-white transition-colors duration-300" />
                </motion.div>
                <h3 className="text-lg font-semibold text-white mb-2 font-heading">
                  {feature.title}
                </h3>
                <p className="text-sm text-(--color-muted-gray) leading-relaxed">
                  {feature.description}
                </p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// How It Works Section with Animation
const HowItWorksSection = () => {
  const steps = [
    {
      step: '01',
      title: 'Connect WhatsApp',
      description: 'Link your WhatsApp Business API account in minutes with our guided setup.',
    },
    {
      step: '02',
      title: 'Import Contacts',
      description: 'Upload your contacts or sync from your CRM with automatic segmentation.',
    },
    {
      step: '03',
      title: 'Create Campaigns',
      description: 'Design beautiful message templates and schedule your campaigns.',
    },
    {
      step: '04',
      title: 'Track & Optimize',
      description: 'Monitor real-time analytics and optimize for better engagement.',
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-(--color-dark-navy)">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div 
          className="text-center max-w-2xl mx-auto mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <Badge variant="orange" className="mb-4">How It Works</Badge>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
            Get Started in
            <span className="text-gradient"> 4 Simple Steps</span>
          </h2>
          <p className="text-(--color-muted-gray)">
            Our streamlined process gets you up and running quickly.
          </p>
        </motion.div>

        {/* Steps */}
        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-0"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          {steps.map((item, index) => (
            <motion.div 
              key={index} 
              className="relative text-center"
              variants={fadeInUp}
            >
              {/* Connector line between circles */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-8 left-[calc(50%+40px)] right-[calc(-50%+40px)] h-[2px] z-0">
                  <motion.div
                    className="h-full bg-(--color-signal-orange)/40"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 + 0.5, duration: 0.6, ease: "easeInOut" }}
                    style={{ originX: 0 }}
                  />
                  <motion.div
                    className="absolute right-0 top-1/2 -translate-y-1/2"
                    initial={{ opacity: 0, x: -8 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.2 + 1, duration: 0.3 }}
                  >
                    <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
                      <path d="M1 1L6 6L1 11" stroke="var(--color-signal-orange)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </motion.div>
                </div>
              )}
              
              {/* Step Number */}
              <motion.div 
                className={`relative z-10 w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center shadow-orange ring-4 ${
                  index === 0
                    ? 'bg-[#25D366] ring-[#25D366]/25'
                    : 'bg-linear-to-br from-(--color-signal-orange) to-(--color-warm-amber) ring-(--color-signal-orange)/20'
                }`}
                whileHover={{ scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {index === 0 ? (
                  <WhatsAppIcon size={26} className="text-white" />
                ) : (
                  <span className="text-xl font-bold text-white font-heading">{item.step}</span>
                )}
              </motion.div>
              
              <h3 className="text-lg font-semibold text-white mb-2 font-heading">
                {item.title}
              </h3>
              <p className="text-sm text-(--color-muted-gray) leading-relaxed max-w-[220px] mx-auto">
                {item.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Preview Section with Animation (Dynamic from API)
const PricingPreviewSection = () => {
  const { plans, isLoading, error } = usePlans();

  // Plan names mapping for display
  const planDisplayNames: Record<string, string> = {
    STARTER: 'Starter',
    STANDARD: 'Standard',
    PROFESSIONAL: 'Professional',
    ENTERPRISE: 'Enterprise',
  };

  // Plan descriptions fallback
  const planDescriptions: Record<string, string> = {
    STARTER: 'Perfect for small businesses',
    STANDARD: 'Great for growing teams',
    PROFESSIONAL: 'For scaling businesses',
    ENTERPRISE: 'Complete solution',
  };

  // Format price
  const formatPrice = (price: number | string): string => {
    return Number(price).toFixed(2);
  };

  // Show all plans
  const displayPlans = plans;

  return (
    <section className="py-20 lg:py-28">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div 
          className="text-center max-w-2xl mx-auto mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <Badge variant="orange" className="mb-4">Pricing</Badge>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
            Simple, Transparent
            <span className="text-gradient"> Pricing</span>
          </h2>
          <p className="text-(--color-muted-gray)">
            Choose the plan that fits your business needs. All plans include core features.
          </p>
        </motion.div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-(--color-signal-orange) border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Error State - Show fallback message */}
        {error && !isLoading && (
          <div className="text-center py-12">
            <p className="text-(--color-muted-gray) mb-4">Unable to load pricing plans</p>
            <Link to={ROUTES.PRICING}>
              <Button variant="secondary">View Pricing Page</Button>
            </Link>
          </div>
        )}

        {/* Pricing Cards */}
        {!isLoading && !error && displayPlans.length > 0 && (
          <motion.div 
            className={cn(
              'grid gap-6 lg:gap-8',
              displayPlans.length === 1 ? 'max-w-md mx-auto' :
              displayPlans.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' :
              displayPlans.length === 3 ? 'md:grid-cols-3 max-w-5xl mx-auto' :
              'md:grid-cols-2 lg:grid-cols-4'
            )}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
          >
            {displayPlans.map((plan) => (
              <motion.div
                key={plan.id}
                variants={scaleIn}
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <Card
                  variant={plan.isPopular ? 'highlight' : 'default'}
                  className={cn('relative h-full', plan.isPopular && 'scale-105 lg:scale-110')}
                >
                  {plan.isPopular && (
                    <motion.div
                      initial={{ y: -20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      <Badge variant="orange" className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap">
                        Most Popular
                      </Badge>
                    </motion.div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="text-lg font-semibold text-white mb-2 font-heading">
                      {planDisplayNames[plan.name] || plan.name}
                    </h3>
                    <p className="text-sm text-(--color-muted-gray) mb-4">
                      {plan.description || planDescriptions[plan.name]}
                    </p>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-white font-heading">
                        ${formatPrice(plan.monthlyPrice)}
                      </span>
                      <span className="text-(--color-muted-gray)">/month</span>
                    </div>
                  </div>
                  
                  {plan.features && plan.features.length > 0 && (
                    <ul className="space-y-3 mb-6">
                      {plan.features.slice(0, 5).map((feature, idx) => (
                        <motion.li 
                          key={idx} 
                          className="flex items-center gap-3 text-sm text-(--color-soft-gray)"
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.1 }}
                        >
                          <CheckCircle2 size={16} className="text-(--color-success) shrink-0" />
                          {feature}
                        </motion.li>
                      ))}
                    </ul>
                  )}
                  
                  <Link to={ROUTES.AUTH.REGISTER}>
                    <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Button
                        variant={plan.isPopular ? 'primary' : 'secondary'}
                        fullWidth
                      >
                        Get Started
                      </Button>
                    </motion.div>
                  </Link>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* View All Link */}
        <motion.div 
          className="text-center mt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <Link to={ROUTES.PRICING} className="link-orange inline-flex items-center gap-2">
            View all plans <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section with Animation
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "AmplifyMsg has completely streamlined how we communicate with our donors and beneficiaries. The WhatsApp campaigns reach people instantly, and the engagement we get is far beyond what email ever delivered.",
      author: 'Rab Nawaz',
      role: 'CFO',
      company: 'Sundas Foundation',
      rating: 5,
    },
    {
      quote: "Managing outreach across multiple programs used to be a logistical nightmare. With AmplifyMsg, we run targeted WhatsApp campaigns in minutes and track every response it has been a game changer for our operations.",
      author: 'Umair Khan',
      role: 'Director',
      company: 'Hopewell Foundation',
      rating: 5,
    },
    {
      quote: "Patient follow ups and appointment reminders through AmplifyMsg have drastically reduced our no-show rates. The platform is reliable, easy to use, and the support team is always responsive when we need help.",
      author: 'Dr. Adnan',
      role: 'Medical Director',
      company: 'Zarya Begum Foundation',
      rating: 5,
    },
    {
      quote: "We rely on AmplifyMsg to keep our donors informed and our field teams connected. The bulk messaging and automation features save us hours every week, letting us focus on what truly matters saving lives.",
      author: 'Wajid Shafi',
      role: 'Project Director',
      company: 'Renal Care Foundation',
      rating: 5,
    },
    {
      quote: "AmplifyMsg gave us the ability to coordinate across departments effortlessly. From donor updates to internal alerts, the platform handles it all with speed and precision it has become an essential part of our daily operations.",
      author: 'Abdul Sattar',
      role: 'COO',
      company: 'Sundas Foundation',
      rating: 5,
    },
    {
      quote: "Managing day to day communications with thousands of beneficiaries was overwhelming before AmplifyMsg. Now, scheduling campaigns and sending instant updates is seamless it has simplified our administrative workload tremendously.",
      author: 'M. Yaqoob',
      role: 'Administrator',
      company: 'Sundas Foundation',
      rating: 5,
    },
  ];

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  const scroll = useCallback((direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector<HTMLElement>('[data-card]')?.offsetWidth || 340;
    const gap = 32;
    el.scrollBy({ left: direction === 'left' ? -(cardWidth + gap) : (cardWidth + gap), behavior: 'smooth' });
    setTimeout(checkScroll, 400);
  }, [checkScroll]);

  return (
    <section className="py-20 lg:py-28 bg-(--color-dark-navy) overflow-hidden">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div 
          className="text-center max-w-2xl mx-auto mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <Badge variant="orange" className="mb-4">Testimonials</Badge>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
            Loved by Businesses
            <span className="text-gradient"> Worldwide</span>
          </h2>
          <p className="text-(--color-muted-gray)">
            See what our customers have to say about AmplifyMsg.
          </p>
        </motion.div>
      </div>

      {/* Scrollable Testimonials — full width */}
      <div className="relative">
        {/* Left arrow */}
        {canScrollLeft && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => scroll('left')}
            className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-(--color-signal-orange) text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform cursor-pointer"
          >
            <ChevronLeft size={20} />
          </motion.button>
        )}

        {/* Right arrow */}
        {canScrollRight && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={() => scroll('right')}
            className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-(--color-signal-orange) text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform cursor-pointer"
          >
            <ChevronRight size={20} />
          </motion.button>
        )}

        <motion.div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-6 overflow-x-auto scroll-smooth pb-4 px-6 lg:px-12"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                data-card
                variants={fadeInUp}
                whileHover={{ y: -5 }}
                className="min-w-[300px] max-w-[340px] shrink-0"
              >
                <Card className="flex flex-col h-full">
                  {/* Stars */}
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                      >
                        <Star size={16} className="text-(--color-signal-orange) fill-current" />
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <blockquote className="text-(--color-soft-gray) leading-relaxed mb-6 flex-1 text-sm">
                    "{testimonial.quote}"
                  </blockquote>
                  
                  {/* Author */}
                  <div className="flex items-center gap-3">
                    <motion.div 
                      className="w-10 h-10 rounded-full bg-linear-to-br from-(--color-signal-orange) to-(--color-warm-amber) flex items-center justify-center shrink-0"
                      whileHover={{ scale: 1.1 }}
                    >
                      <span className="text-sm font-semibold text-white">
                        {testimonial.author.split(' ').map(n => n[0]).join('')}
                      </span>
                    </motion.div>
                    <div>
                      <p className="text-sm font-medium text-white">{testimonial.author}</p>
                      <p className="text-xs text-(--color-muted-gray)">
                        {testimonial.role}, {testimonial.company}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
    </section>
  );
};

// CTA Section with Animation
const CTASection = () => (
  <section className="py-20 lg:py-28 relative overflow-hidden">
    {/* Background */}
    <div className="absolute inset-0 bg-linear-to-r from-(--color-signal-orange)/10 via-transparent to-(--color-warm-amber)/10" />
    <motion.div 
      className="absolute top-0 left-1/4 w-64 h-64 bg-(--color-signal-orange)/20 rounded-full blur-3xl"
      animate={{ 
        x: [-20, 20, -20],
        y: [-10, 10, -10]
      }}
      transition={{ duration: 8, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
    />
    <motion.div 
      className="absolute bottom-0 right-1/4 w-64 h-64 bg-(--color-warm-amber)/20 rounded-full blur-3xl"
      animate={{ 
        x: [20, -20, 20],
        y: [10, -10, 10]
      }}
      transition={{ duration: 8, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
    />

    {/* Floating WhatsApp icons */}
    <motion.div
      className="absolute top-12 left-[10%] hidden lg:block"
      animate={{ y: [-10, 10, -10], rotate: [-8, 8, -8] }}
      transition={{ duration: 6, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95] }}
    >
      <div className="p-3 rounded-2xl bg-[#25D366]/10 border border-[#25D366]/20">
        <WhatsAppIcon size={24} className="text-[#25D366]" />
      </div>
    </motion.div>
    <motion.div
      className="absolute bottom-16 right-[12%] hidden lg:block"
      animate={{ y: [8, -8, 8], rotate: [5, -5, 5] }}
      transition={{ duration: 7, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95], delay: 1 }}
    >
      <div className="p-3 rounded-2xl bg-[#25D366]/10 border border-[#25D366]/20">
        <WhatsAppIcon size={20} className="text-[#25D366]" />
      </div>
    </motion.div>
    <motion.div
      className="absolute top-1/2 right-[5%] hidden xl:block"
      animate={{ y: [-6, 6, -6], x: [-4, 4, -4] }}
      transition={{ duration: 5, repeat: Infinity, ease: [0.45, 0.05, 0.55, 0.95], delay: 0.5 }}
    >
      <div className="p-2 rounded-xl bg-[#25D366]/10 border border-[#25D366]/20">
        <WhatsAppIcon size={16} className="text-[#25D366]" />
      </div>
    </motion.div>
    
    <div className="container-custom relative z-10">
      <motion.div 
        className="max-w-3xl mx-auto text-center"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <motion.h2 
          className="text-3xl lg:text-4xl xl:text-5xl font-heading font-bold text-white mb-6"
          variants={fadeInUp}
        >
          Ready to Amplify Your
          <motion.span 
            className="text-gradient block mt-2"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Business Growth?
          </motion.span>
        </motion.h2>
        <motion.p 
          className="text-lg text-(--color-soft-gray) mb-8 max-w-xl mx-auto"
          variants={fadeInUp}
        >
          Join thousands of businesses already using AmplifyMsg to transform their customer communications.
        </motion.p>
        <motion.div 
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
          variants={fadeInUp}
        >
          <Link to={ROUTES.AUTH.REGISTER}>
            <motion.div whileHover={hoverScale} whileTap={tapScale}>
              <Button variant="primary" size="lg" rightIcon={<ArrowRight size={18} />}>
                Get Started Now
              </Button>
            </motion.div>
          </Link>
          <Link to={ROUTES.CONTACT}>
            <motion.div whileHover={hoverScale} whileTap={tapScale}>
              <Button variant="secondary" size="lg">
                Talk to Sales
              </Button>
            </motion.div>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// Main HomePage Component
const HomePage = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <HeroSection />
      <StatsSection />
      <WhatsAppSuiteSlider />
      <ClientsMarquee />
      <FeaturesSection />
      <HowItWorksSection />
      <PricingPreviewSection />
      <TestimonialsSection />
      <CTASection />
    </motion.div>
  );
};

export { HomePage };
